import React, { useState, useEffect } from 'react';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';

/**
 * QuantitySelector component for selecting product quantities
 * with +/- controls and validation
 */
const QuantitySelector = ({
  value = 1,
  onChange,
  min = 1,
  max = Number.MAX_SAFE_INTEGER,
  stock = null,
  showAvailable = true,
  className = '',
  disabled = false
}) => {
  const [quantity, setQuantity] = useState(value);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);

  // Determine the actual maximum quantity allowed
  const maxQuantity = stock !== null ? Math.min(max, stock) : max;
  
  // Update local state when the value prop changes
  useEffect(() => {
    setQuantity(value);
  }, [value]);

  // Format error messages
  const getErrorMessage = (action, limit) => {
    if (action === 'increment' && limit === stock) {
      return `Cannot add more. Only ${stock} available.`;
    } else if (action === 'decrement' && limit === min) {
      return `Minimum quantity is ${min}.`;
    }
    return '';
  };

  // Show error for 3 seconds then hide it
  useEffect(() => {
    if (errorMessage) {
      setShowError(true);
      const timer = setTimeout(() => {
        setShowError(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const handleIncrement = () => {
    if (quantity < maxQuantity) {
      const newValue = quantity + 1;
      setQuantity(newValue);
      onChange && onChange(newValue);
      setErrorMessage('');
    } else {
      setErrorMessage(getErrorMessage('increment', maxQuantity));
    }
  };

  const handleDecrement = () => {
    if (quantity > min) {
      const newValue = quantity - 1;
      setQuantity(newValue);
      onChange && onChange(newValue);
      setErrorMessage('');
    } else {
      setErrorMessage(getErrorMessage('decrement', min));
    }
  };

  const handleInputChange = (e) => {
    let newValue = parseInt(e.target.value, 10);
    
    // Handle non-numeric or empty input
    if (isNaN(newValue)) {
      newValue = min;
    }

    // Enforce min/max constraints
    if (newValue > maxQuantity) {
      newValue = maxQuantity;
      setErrorMessage(getErrorMessage('increment', maxQuantity));
    } else if (newValue < min) {
      newValue = min;
      setErrorMessage(getErrorMessage('decrement', min));
    } else {
      setErrorMessage('');
    }

    setQuantity(newValue);
    onChange && onChange(newValue);
  };

  return (
    <div className={`space-y-2 ${className} mb-2`}>
      <label className="block text-sm font-medium text-gray-700">Quantity</label>
      <div className="relative">
        <div className="flex rounded-md">
          <button
            type="button"
            onClick={handleDecrement}
            // disabled={quantity <= min || disabled}
            className="relative inline-flex items-center justify-center rounded-l-md bg-gray-50 px-4 py-2 text-gray-400 hover:bg-gray-100 focus:z-10 focus:outline-none focus:ring-1 focus:ring-brand-teal disabled:opacity-50 disabled:hover:bg-gray-50"
          >
            <MinusIcon className="h-4 w-4" />
          </button>
          <input
            type="text"
            value={quantity}
            onChange={handleInputChange}
            disabled={disabled}
            className="block w-20 border-0 py-1.5 text-center text-gray-900 focus:ring-0 sm:text-sm sm:leading-6"
            min={min}
            max={maxQuantity}
          />
          <button
            type="button"
            onClick={handleIncrement}
            // disabled={quantity >= maxQuantity || disabled}
            className="relative inline-flex items-center justify-center rounded-r-md bg-gray-50 px-4 py-2 text-gray-400 hover:bg-gray-100 focus:z-10 focus:outline-none focus:ring-1 focus:ring-brand-teal disabled:opacity-50 disabled:hover:bg-gray-50"
          >
            <PlusIcon className="h-4 w-4" />
          </button>

           {/* Available stock message */}
        {showAvailable && stock !== null && (
          <div className="relative inline-flex items-center justify-center px-4 py-2 text-sm text-gray-500 ">
            {stock} available
          </div>
        )}
        </div>
        
       
        
        {/* Error message */}
        {showError && errorMessage && (
          <div className="mt-1 text-sm text-red-600">
            {errorMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuantitySelector; 